import { hasAnyPermission, useAuthorization } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { formatPlaceholderToken } from '@frontend/html-template-editor';
import { NotificationContentClient, NotificationTemplateClient, NotificationTemplatePlaceholderClient } from '@frontend/notification/api';
import { NotificationContent, NotificationTemplate } from '@frontend/notification/types';
import { useAccountRepository } from '@frontend/repository';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useContentDetail = () => {
    const { currentAccount } = useAccountRepository();
    const { permissions } = useAuthorization();
    const navigate = useNavigate();
    const { accountId, templateId, contentId } = useParams();
    const workingAccountId = accountId || currentAccount;
    const [template, setTemplate] = useState<NotificationTemplate | null>(null);
    const [content, setContent] = useState<NotificationContent | null>(null);
    const [placeholders, setPlaceholders] = useState<string[]>([]);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        NotificationTemplatePlaceholderClient.fetchPlaceholders().then(setPlaceholders);
    }, []);

    useEffect(() => {
        if (!workingAccountId || !templateId || !contentId) return;
        let isMounted = true;
        setHasError(false);
        Promise.all([
            NotificationTemplateClient.fetchAccountNotificationTemplate(workingAccountId, templateId),
            NotificationContentClient.fetchAccountNotificationContent(workingAccountId, templateId, contentId)
        ])
            .then(([nextTemplate, nextContent]) => {
                if (!isMounted) return;
                setTemplate(nextTemplate);
                setContent(nextContent);
            })
            .catch(() => {
                if (isMounted) setHasError(true);
            });

        return () => {
            isMounted = false;
        };
    }, [workingAccountId, templateId, contentId]);

    function onDragStart(event: React.DragEvent) {
        const eventTarget = event.target as any;
        const target = eventTarget.nodeType === 1 ? eventTarget : eventTarget.parentElement;
        const draggable = target.closest('[draggable]');
        const found = draggable?.dataset?.placeholder;
        if (!found) return;

        event.dataTransfer?.setData('text/plain', formatPlaceholderToken(found));
        event.dataTransfer?.setData('text/html', formatPlaceholderToken(found));
    }

    const notificationTemplatePermissions = (right: Rights) => [
        { item: EntityType.NOTIFICATION_TEMPLATE, source: EntityType.ACCOUNT, right, uid: workingAccountId },
        { item: EntityType.NOTIFICATION_TEMPLATE, source: EntityType.NOTIFICATION_TEMPLATE, right, uid: templateId! }
    ];

    const canReadNotificationTemplate =
        permissions != null && templateId != null && hasAnyPermission(permissions, notificationTemplatePermissions(Rights.READ));
    const canUpdateNotificationTemplate =
        permissions != null && templateId != null && hasAnyPermission(permissions, notificationTemplatePermissions(Rights.UPDATE));

    const onCancel = () => {
        if (templateId) {
            navigate(`/notifications/templates/${templateId}/details`);
            return;
        }
        navigate(-1);
    };

    return {
        template,
        content,
        hasError,
        placeholders,
        onDragStart,
        onCancel,
        currentAccount: workingAccountId,
        canReadNotificationTemplate,
        canUpdateNotificationTemplate
    };
};
